import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AnnotatedImageExportOptions,
  AnnotationColor,
  AnnotationPoint,
  AnnotationTool,
  AnnotationWidth,
  BlurAnnotation,
  DEFAULT_ANNOTATION_COLORS,
  DEFAULT_ANNOTATION_WIDTHS,
  ImageAnnotation,
  ImageAnnotationDocument,
  ImageRotation,
  LegacyMarkerAnnotation,
  MarkerAnnotation,
  MarkerSet,
  MeasurementAnnotation,
  MeasurementUnit,
  StrokeAnnotation,
  StrokeMode,
} from './image-annotation-editor.types';
import {
  FloatingPalettePosition,
  clampBlurPosition,
  clampFloatingPalettePosition,
  clampImageViewportPosition,
  getAutomaticMarkerLabelPosition,
  getAdjacentFloatingPanelPosition,
  getLabelConnectionPoint,
  getMeasurementUnitDefinition,
  getMeasurementUnitGroups,
  getInitialFloatingPalettePosition,
  getNextMarkerPlacement,
  getRotatedImageSize,
  mapRotatedPointToOriginal,
  NextMarkerPlacement,
  normalizeAnnotationDocument,
  normalizeMarkerAnnotation,
  rebaseAnnotationDocumentRotation,
} from './image-annotation-editor-config';

type AnnotationPopover = 'color' | 'width' | 'unit' | 'marker' | 'pen' | 'text-marker';

@Component({
  standalone: false,
  selector: 'app-image-annotation-editor',
  templateUrl: './image-annotation-editor.component.html',
  styleUrls: ['./image-annotation-editor.component.scss'],
})
export class ImageAnnotationEditorComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('annotationCanvas') private canvasRef?: ElementRef<HTMLCanvasElement>;
  @ViewChild('surface') private surfaceRef?: ElementRef<HTMLElement>;
  @ViewChild('toolPalette') private toolPaletteRef?: ElementRef<HTMLElement>;
  @ViewChild('floatingPanel') private floatingPanelRef?: ElementRef<HTMLElement>;

  @Input() image: string | Blob | null = null;
  @Input() annotationData: ImageAnnotationDocument | null = null;
  @Input() colors: readonly AnnotationColor[] = DEFAULT_ANNOTATION_COLORS;
  @Input() widths: readonly AnnotationWidth[] = DEFAULT_ANNOTATION_WIDTHS;
  @Input() drawWithTouch = true;
  @Input() maxDisplayHeight = '72dvh';
  @Input() fullscreen = false;

  @Output() annotationDataChange = new EventEmitter<ImageAnnotationDocument>();
  @Output() imageReady = new EventEmitter<{ width: number; height: number }>();
  @Output() editorError = new EventEmitter<unknown>();

  tool: AnnotationTool = 'pan';
  selectedColor = DEFAULT_ANNOTATION_COLORS[0].value;
  selectedWidth = DEFAULT_ANNOTATION_WIDTHS[1].value;
  selectedStrokeMode: StrokeMode = 'freehand';
  activePopover?: AnnotationPopover;
  measurementInput = '';
  selectedMeasurementUnit: MeasurementUnit = 'V';
  selectedMarkerSet: MarkerSet = 'number';
  textMarkerInput = '';
  pendingMeasurementPoint?: AnnotationPoint;
  loading = false;
  errorMessage = '';
  document: ImageAnnotationDocument = this.emptyDocument();
  readonly Math = Math;
  readonly measurementUnitGroups = getMeasurementUnitGroups();
  readonly strokeModes: readonly { id: StrokeMode; label: string; symbol: string }[] = [
    { id: 'freehand', label: '自由線', symbol: '〰' },
    { id: 'line', label: '直線', symbol: '／' },
    { id: 'ellipse', label: '楕円', symbol: '○' },
    { id: 'rectangle', label: '四角', symbol: '□' },
    { id: 'highlighter', label: '蛍光', symbol: '▰' },
  ];
  imageElement?: HTMLImageElement;
  palettePosition?: FloatingPalettePosition;
  floatingPanelPosition?: FloatingPalettePosition;
  paletteDragging = false;
  imageDisplayWidth = 0;
  imageDisplayHeight = 0;
  imageViewScale = 1;
  imageViewX = 0;
  imageViewY = 0;
  selectedAnnotationId?: string;

  private objectUrl?: string;
  private activeStroke?: StrokeAnnotation;
  private activeBlur?: BlurAnnotation;
  private activeBlurOrigin?: AnnotationPoint;
  private activeBlurMove?: {
    pointerId: number;
    annotationId: string;
    offsetX: number;
    offsetY: number;
    startX: number;
    startY: number;
    moved: boolean;
  };
  private activePointerId?: number;
  private pendingTextMarker?: {
    point: AnnotationPoint;
    labelPosition: AnnotationPoint;
    orientation: ImageRotation;
  };
  private cancelTextMarkerRequested = false;
  private textMarkerViewportBaseline?: number;
  private textMarkerLastViewportHeight?: number;
  private textMarkerKeyboardWasVisible = false;
  private readonly visualViewportResizeHandler = (): void => this.handleVisualViewportResize();
  private activeTextMarkerPointDrag?: {
    pointerId: number;
    annotationId: string;
    startX: number;
    startY: number;
    moved: boolean;
  };
  private undoStack: ImageAnnotationDocument[] = [];
  private redoStack: ImageAnnotationDocument[] = [];
  private resizeObserver?: ResizeObserver;
  private viewReady = false;
  private imageLoadSequence = 0;
  private floatingPanelFrame?: number;
  private blurScratchCanvas?: HTMLCanvasElement;
  private textMeasureContext?: CanvasRenderingContext2D;
  private paletteDrag?: {
    pointerId: number;
    offsetX: number;
    offsetY: number;
  };
  private readonly imageViewPointers = new Map<number, { x: number; y: number }>();
  private imageViewGesture?: {
    centerX: number;
    centerY: number;
    distance?: number;
    scale: number;
    anchorX?: number;
    anchorY?: number;
  };
  private activeLabelDrag?: {
    pointerId: number;
    annotationId: string;
    offsetX: number;
    offsetY: number;
    startX: number;
    startY: number;
    moved: boolean;
  };

  constructor(private readonly changeDetector: ChangeDetectorRef) {}

  get imageLoaded(): boolean { return !!this.imageElement && this.document.baseImage.width > 0; }
  get canUndo(): boolean { return this.undoStack.length > 0; }
  get canRedo(): boolean { return this.redoStack.length > 0; }
  get imageViewTransform(): string {
    const scale = this.imageViewScale;
    const width = this.imageDisplayWidth;
    const height = this.imageDisplayHeight;
    switch (this.imageRotation) {
      case 90: return `matrix(0, ${scale}, ${-scale}, 0, ${this.imageViewX + height * scale}, ${this.imageViewY})`;
      case 180: return `matrix(${-scale}, 0, 0, ${-scale}, ${this.imageViewX + width * scale}, ${this.imageViewY + height * scale})`;
      case 270: return `matrix(0, ${-scale}, ${scale}, 0, ${this.imageViewX}, ${this.imageViewY + width * scale})`;
      default: return `matrix(${scale}, 0, 0, ${scale}, ${this.imageViewX}, ${this.imageViewY})`;
    }
  }
  get imageRotation(): ImageRotation { return this.document.rotation ?? 0; }
  get nextMarkerPlacement(): NextMarkerPlacement | undefined {
    return getNextMarkerPlacement(
      this.selectedMarkerSet,
      this.selectedColor,
      this.colors.map(color => color.value),
      this.document.annotations,
    );
  }
  get markerToolbarText(): string {
    return this.selectedMarkerSet === 'text'
      ? 'T'
      : this.nextMarkerPlacement?.marker.text ?? '完';
  }
  get strokeToolbarText(): string {
    return this.strokeModes.find(mode => mode.id === this.selectedStrokeMode)?.symbol ?? '〰';
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    if (this.surfaceRef) {
      this.resizeObserver = new ResizeObserver(() => this.handleSurfaceResize());
      this.resizeObserver.observe(this.surfaceRef.nativeElement);
    }
    window.visualViewport?.addEventListener('resize', this.visualViewportResizeHandler);
    if (this.image) void this.loadImage(this.image);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['image'] && this.viewReady) void this.loadImage(this.image);
    if (
      changes['annotationData']
      && !changes['annotationData'].firstChange
      && this.annotationData
      && JSON.stringify(this.annotationData) !== JSON.stringify(this.document)
    ) {
      this.loadAnnotations(this.annotationData);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    window.visualViewport?.removeEventListener('resize', this.visualViewportResizeHandler);
    if (this.floatingPanelFrame !== undefined) cancelAnimationFrame(this.floatingPanelFrame);
    this.paletteDrag = undefined;
    this.imageViewPointers.clear();
    this.activeLabelDrag = undefined;
    this.blurScratchCanvas = undefined;
    this.textMeasureContext = undefined;
    this.releaseObjectUrl();
  }

  async loadImage(source: string | Blob | null): Promise<void> {
    const sequence = ++this.imageLoadSequence;
    this.loading = true;
    this.errorMessage = '';
    this.releaseObjectUrl();
    if (!source) {
      this.imageElement = undefined;
      this.document = this.emptyDocument();
      this.loading = false;
      this.render();
      return;
    }

    try {
      const src = source instanceof Blob ? (this.objectUrl = URL.createObjectURL(source)) : source;
      const image = await this.loadHtmlImage(src);
      if (sequence !== this.imageLoadSequence) return;
      this.imageElement = image;
      const matchingDocument = this.annotationData
        && this.annotationData.baseImage.width === image.naturalWidth
        && this.annotationData.baseImage.height === image.naturalHeight
        ? normalizeAnnotationDocument(this.annotationData)
        : undefined;
      this.document = {
        version: 1,
        baseImage: { width: image.naturalWidth, height: image.naturalHeight },
        annotations: matchingDocument?.annotations ?? [],
      };
      if (matchingDocument?.rotation) this.document.rotation = matchingDocument.rotation;
      this.undoStack = [];
      this.redoStack = [];
      this.tool = 'pan';
      this.activePopover = undefined;
      this.selectedAnnotationId = undefined;
      this.loading = false;
      this.imageReady.emit({ width: image.naturalWidth, height: image.naturalHeight });
      this.changeDetector.detectChanges();
      this.palettePosition = undefined;
      requestAnimationFrame(() => this.handleSurfaceResize(true));
    } catch (error) {
      if (sequence !== this.imageLoadSequence) return;
      this.loading = false;
      this.errorMessage = '画像を読み込めませんでした。';
      this.editorError.emit(error);
      this.changeDetector.detectChanges();
    }
  }

  loadAnnotations(data: ImageAnnotationDocument | null): void {
    if (!data) {
      this.clearAnnotations();
      return;
    }
    if (data.version !== 1) throw new Error(`Unsupported annotation version: ${String(data.version)}`);
    if (this.imageLoaded && (
      data.baseImage.width !== this.document.baseImage.width
      || data.baseImage.height !== this.document.baseImage.height
    )) {
      throw new Error('Annotation base image size does not match the loaded image.');
    }
    const previousRotation = this.imageRotation;
    this.document = normalizeAnnotationDocument(data);
    this.undoStack = [];
    this.redoStack = [];
    if (previousRotation !== this.imageRotation) {
      this.layoutImageInViewport();
      this.changeDetector.detectChanges();
      requestAnimationFrame(() => this.resizeCanvas());
    }
    this.render();
  }

  getAnnotations(): ImageAnnotationDocument { return this.cloneDocument(this.document); }

  clearAnnotations(): void {
    if (!this.document.annotations.length) return;
    this.recordHistory();
    this.document = { ...this.document, annotations: [] };
    this.selectedAnnotationId = undefined;
    this.renderAndEmit();
  }

  undo(): void {
    const previous = this.undoStack.pop();
    if (!previous) return;
    const current = this.cloneDocument(this.document);
    const previousIds = new Set(previous.annotations.map(annotation => annotation.id));
    const revertedMarker = current.annotations.find(annotation => annotation.type === 'marker' && !previousIds.has(annotation.id));
    this.redoStack.push(current);
    const rotationChanged = (current.rotation ?? 0) !== (previous.rotation ?? 0);
    this.document = previous;
    if (revertedMarker?.type === 'marker') {
      this.selectedMarkerSet = revertedMarker.markerSet;
      this.selectedColor = revertedMarker.color;
    }
    if (rotationChanged) {
      this.layoutImageInViewport();
      this.changeDetector.detectChanges();
      requestAnimationFrame(() => this.resizeCanvas());
    }
    this.renderAndEmit();
  }

  redo(): void {
    const next = this.redoStack.pop();
    if (!next) return;
    const current = this.cloneDocument(this.document);
    const currentIds = new Set(current.annotations.map(annotation => annotation.id));
    const restoredMarker = next.annotations.find(annotation => annotation.type === 'marker' && !currentIds.has(annotation.id));
    this.undoStack.push(current);
    const rotationChanged = (current.rotation ?? 0) !== (next.rotation ?? 0);
    this.document = next;
    if (restoredMarker?.type === 'marker') {
      this.selectedMarkerSet = restoredMarker.markerSet;
      this.selectedColor = restoredMarker.color;
      const nextPlacement = this.nextMarkerPlacement;
      if (nextPlacement) this.selectedColor = nextPlacement.color;
    }
    if (rotationChanged) {
      this.layoutImageInViewport();
      this.changeDetector.detectChanges();
      requestAnimationFrame(() => this.resizeCanvas());
    }
    this.renderAndEmit();
  }

  async exportAnnotatedImage(options: AnnotatedImageExportOptions = {}): Promise<Blob> {
    return this.exportImage(options, true);
  }

  async exportRotatedSourceImage(options: AnnotatedImageExportOptions = {}): Promise<Blob> {
    return this.exportImage(options, false);
  }

  getAnnotationsRebasedToRotatedSource(): ImageAnnotationDocument {
    return rebaseAnnotationDocumentRotation(this.document);
  }

  private exportImage(options: AnnotatedImageExportOptions, includeAnnotations: boolean): Promise<Blob> {
    if (!this.imageElement || !this.imageLoaded) throw new Error('No image has been loaded.');
    const canvas = document.createElement('canvas');
    const { width, height } = this.document.baseImage;
    const swapsDimensions = this.imageRotation === 90 || this.imageRotation === 270;
    canvas.width = swapsDimensions ? height : width;
    canvas.height = swapsDimensions ? width : height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas 2D context is unavailable.');
    context.save();
    this.applyImageRotation(context, this.imageRotation, width, height);
    context.drawImage(this.imageElement, 0, 0, width, height);
    if (includeAnnotations) this.drawAnnotations(context, this.document.annotations);
    context.restore();
    const type = options.type ?? 'image/jpeg';
    const quality = Math.min(1, Math.max(0, options.quality ?? 0.92));
    return new Promise<Blob>((resolve, reject) => {
      try {
        canvas.toBlob(
          blob => blob ? resolve(blob) : reject(new Error('Image export failed.')),
          type,
          quality,
        );
      } catch (error) {
        reject(error);
      }
    });
  }

  selectTool(tool: AnnotationTool): void {
    this.tool = tool;
    this.activePopover = undefined;
    this.cancelMeasurement();
  }

  togglePopover(popover: AnnotationPopover): void {
    if (this.pendingMeasurementPoint) this.cancelMeasurement();
    if (popover === 'unit') this.tool = 'measurement';
    if (popover === 'marker') this.tool = 'marker';
    if (popover === 'pen') this.tool = 'pen';
    this.activePopover = this.activePopover === popover ? undefined : popover;
    if (this.activePopover) this.scheduleFloatingPanelPosition();
  }

  selectColor(color: string): void { this.selectedColor = color; this.activePopover = undefined; }
  selectWidth(width: number): void { this.selectedWidth = width; this.activePopover = undefined; }
  selectStrokeMode(mode: StrokeMode): void {
    this.selectedStrokeMode = mode;
    this.tool = 'pen';
    this.activePopover = undefined;
  }
  selectMeasurementUnit(unit: MeasurementUnit): void {
    this.selectedMeasurementUnit = unit;
    this.tool = 'measurement';
    this.activePopover = undefined;
    this.cancelMeasurement();
  }
  selectMarkerSet(markerSet: MarkerSet): void {
    this.selectedMarkerSet = markerSet;
    this.tool = 'marker';
    this.activePopover = undefined;
    this.cancelMeasurement();
  }

  openTextMarkerInput(): void {
    if (this.pendingMeasurementPoint) this.cancelMeasurement();
    this.selectedMarkerSet = 'text';
    this.tool = 'marker';
    this.activePopover = undefined;
  }

  onTextMarkerInput(event: Event): void {
    this.updateTextMarkerInput((event.currentTarget as HTMLInputElement).value);
  }

  private updateTextMarkerInput(value: string): void {
    this.textMarkerInput = value;
    const pending = this.pendingTextMarker;
    if (!pending) return;
    const previewText = this.normalizedTextMarkerInput || 'テキスト';
    const preview: MarkerAnnotation = {
      id: 'pending-text-marker',
      type: 'marker',
      markerSet: 'text',
      value: previewText,
      text: previewText,
      color: this.selectedColor,
      point: pending.point,
      labelPosition: pending.labelPosition,
      orientation: pending.orientation,
    };
    pending.labelPosition = this.clampLabelPosition(
      preview,
      pending.labelPosition.x,
      pending.labelPosition.y,
    );
    this.render();
  }

  confirmTextMarkerFromKeyboard(event: Event): void {
    const input = event.currentTarget as HTMLInputElement;
    // iOS IMEはkeydown後に確定文字をinputへ反映する場合があるため、次のtaskで読み直す。
    setTimeout(() => {
      this.updateTextMarkerInput(input.value);
      this.confirmTextMarker();
      input.blur();
    });
  }

  confirmTextMarkerOnBlur(): void {
    if (this.cancelTextMarkerRequested) return;
    // iPhoneの「完了」はEnterではなくキーボードを閉じるだけの場合がある。
    // 最後のionInputを取り込んだ後で、現在の入力内容を自動確定する。
    queueMicrotask(() => this.confirmTextMarker());
  }

  prepareTextMarkerCancel(): void {
    // ボタン押下ではblurがclickより先に発生するため、自動確定を先に抑止する。
    this.cancelTextMarkerRequested = true;
  }

  confirmTextMarker(): void {
    // Canvasのラベルは1行描画のため、改行を空白へ正規化して保存する。
    const text = this.normalizedTextMarkerInput;
    const pending = this.pendingTextMarker;
    if (!text || !pending) return;
    this.recordHistory();
    this.document.annotations.push({
      id: this.createId(),
      type: 'marker',
      markerSet: 'text',
      value: text,
      text,
      color: this.selectedColor,
      point: pending.point,
      labelPosition: pending.labelPosition,
      orientation: pending.orientation,
    });
    this.pendingTextMarker = undefined;
    this.textMarkerInput = '';
    this.cancelTextMarkerRequested = false;
    this.resetTextMarkerKeyboardTracking();
    this.activePopover = undefined;
    this.renderAndEmit();
    this.changeDetector.markForCheck();
  }

  cancelTextMarkerInput(): void {
    this.pendingTextMarker = undefined;
    this.textMarkerInput = '';
    this.cancelTextMarkerRequested = false;
    this.resetTextMarkerKeyboardTracking();
    this.activePopover = undefined;
    this.render();
    this.changeDetector.markForCheck();
  }

  onPointerDown(event: PointerEvent): void {
    if (!this.imageLoaded) return;
    const point = this.toImagePoint(event);
    // キーボードを閉じた後もblurしないiOSでは、写真のタップを確定操作として扱う。
    if (this.pendingTextMarker) {
      if (this.normalizedTextMarkerInput) this.confirmTextMarker();
      else this.cancelTextMarkerInput();
      return;
    }
    if (this.tool === 'blur' && point && this.startBlurMove(event, point)) return;
    if (point && this.startTextMarkerPointDrag(event, point)) return;
    if (this.tool !== 'blur' && point && this.startLabelDrag(event, point)) return;
    if (this.selectedAnnotationId) {
      this.selectedAnnotationId = undefined;
      this.render();
    }
    if (this.tool === 'pan') {
      this.startImageViewGesture(event);
      return;
    }
    if (event.pointerType === 'touch' && !this.drawWithTouch) return;
    if (!point) return;
    event.preventDefault();

    if (this.tool === 'eraser') {
      this.eraseAt(point);
      return;
    }
    if (this.tool === 'blur') {
      this.recordHistory();
      const blur: BlurAnnotation = {
        id: this.createId(),
        type: 'blur',
        x: point.x,
        y: point.y,
        width: 0,
        height: 0,
        radius: Math.max(18, Math.min(this.document.baseImage.width, this.document.baseImage.height) * 0.02),
      };
      this.activeBlur = blur;
      this.activeBlurOrigin = point;
      this.activePointerId = event.pointerId;
      this.document.annotations.push(blur);
      this.canvasRef?.nativeElement.setPointerCapture(event.pointerId);
      this.render();
      return;
    }
    if (this.tool === 'measurement') {
      // 単位メニュー以外をタップした場合は現在の単位を採用し、テンキーだけを表示する。
      this.activePopover = undefined;
      this.pendingMeasurementPoint = point;
      this.measurementInput = '';
      this.scheduleFloatingPanelPosition();
      return;
    }
    if (this.tool === 'marker') {
      this.activePopover = undefined;
      if (this.selectedMarkerSet === 'text') {
        const orientation = this.orientationForCurrentView();
        const labelPosition = getAutomaticMarkerLabelPosition(
          point,
          this.document.baseImage.width,
          this.document.baseImage.height,
          this.getOccupiedLabelPositions(),
        );
        const preview: MarkerAnnotation = {
          id: 'pending-text-marker',
          type: 'marker',
          markerSet: 'text',
          value: 'テキスト',
          text: 'テキスト',
          color: this.selectedColor,
          point,
          labelPosition,
          orientation,
        };
        preview.labelPosition = this.clampLabelPosition(preview, labelPosition.x, labelPosition.y);
        this.pendingTextMarker = {
          point,
          labelPosition: preview.labelPosition,
          orientation,
        };
        this.textMarkerInput = '';
        this.cancelTextMarkerRequested = false;
        this.textMarkerViewportBaseline = window.visualViewport?.height ?? window.innerHeight;
        this.textMarkerLastViewportHeight = this.textMarkerViewportBaseline;
        this.textMarkerKeyboardWasVisible = false;
        this.activePopover = 'text-marker';
        this.render();
        this.scheduleFloatingPanelPosition();
        return;
      }
      const placement = this.nextMarkerPlacement;
      if (!placement) return;
      this.selectedColor = placement.color;
      this.recordHistory();
      const marker: MarkerAnnotation = {
        id: this.createId(),
        type: 'marker',
        markerSet: placement.marker.markerSet,
        value: placement.marker.value,
        text: placement.marker.text,
        color: placement.color,
        point,
        labelPosition: getAutomaticMarkerLabelPosition(
          point,
          this.document.baseImage.width,
          this.document.baseImage.height,
          this.getOccupiedLabelPositions(),
        ),
        orientation: this.orientationForCurrentView(),
      };
      this.document.annotations.push(marker);
      const nextPlacement = this.nextMarkerPlacement;
      if (nextPlacement) this.selectedColor = nextPlacement.color;
      this.renderAndEmit();
      return;
    }

    this.recordHistory();
    const stroke: StrokeAnnotation = {
      id: this.createId(), type: 'stroke', tool: 'pen', color: this.selectedColor,
      width: this.selectedStrokeMode === 'highlighter'
        ? Math.max(24, this.selectedWidth * 2.2)
        : this.selectedWidth,
      opacity: this.selectedStrokeMode === 'highlighter' ? 0.32 : 1,
      points: [point],
      mode: this.selectedStrokeMode,
    };
    this.activeStroke = stroke;
    this.activePointerId = event.pointerId;
    this.document.annotations.push(stroke);
    this.canvasRef?.nativeElement.setPointerCapture(event.pointerId);
    this.render();
  }

  onPointerMove(event: PointerEvent): void {
    if (this.activeTextMarkerPointDrag?.pointerId === event.pointerId) {
      this.moveTextMarkerPointDrag(event);
      return;
    }
    if (this.activeBlurMove?.pointerId === event.pointerId) {
      this.moveBlur(event);
      return;
    }
    if (this.activeLabelDrag?.pointerId === event.pointerId) {
      this.moveLabelDrag(event);
      return;
    }
    if (this.tool === 'pan') {
      this.moveImageViewGesture(event);
      return;
    }
    if (this.activeBlur && this.activeBlurOrigin && this.activePointerId === event.pointerId) {
      const point = this.toImagePoint(event);
      if (!point) return;
      this.activeBlur.x = Math.min(this.activeBlurOrigin.x, point.x);
      this.activeBlur.y = Math.min(this.activeBlurOrigin.y, point.y);
      this.activeBlur.width = Math.abs(point.x - this.activeBlurOrigin.x);
      this.activeBlur.height = Math.abs(point.y - this.activeBlurOrigin.y);
      this.render();
      event.preventDefault();
      return;
    }
    if (!this.activeStroke || this.activePointerId !== event.pointerId) return;
    const point = this.toImagePoint(event);
    if (!point) return;
    event.preventDefault();
    if (this.activeStroke.mode === 'freehand') {
      this.activeStroke.points.push(point);
    } else if (this.activeStroke.points.length === 1) {
      this.activeStroke.points.push(point);
    } else {
      this.activeStroke.points[1] = point;
    }
    this.render();
  }

  onPointerUp(event: PointerEvent): void {
    if (this.activeTextMarkerPointDrag?.pointerId === event.pointerId) {
      this.endTextMarkerPointDrag(event);
      return;
    }
    if (this.activeBlurMove?.pointerId === event.pointerId) {
      this.endBlurMove(event);
      return;
    }
    if (this.activeLabelDrag?.pointerId === event.pointerId) {
      this.endLabelDrag(event);
      return;
    }
    if (this.imageViewPointers.has(event.pointerId)) {
      this.endImageViewGesture(event);
      return;
    }
    if (this.activeBlur && this.activePointerId === event.pointerId) {
      const minimumSize = this.screenPixelsToImage(6);
      const keepBlur = this.activeBlur.width >= minimumSize && this.activeBlur.height >= minimumSize;
      if (!keepBlur) {
        this.document.annotations = this.document.annotations.filter(item => item.id !== this.activeBlur?.id);
        this.undoStack.pop();
      }
      this.activeBlur = undefined;
      this.activeBlurOrigin = undefined;
      this.activePointerId = undefined;
      this.render();
      if (keepBlur) this.emitChange();
      event.preventDefault();
      return;
    }
    if (!this.activeStroke || this.activePointerId !== event.pointerId) return;
    event.preventDefault();
    if (this.activeStroke.points.length === 1) this.activeStroke.points.push({ ...this.activeStroke.points[0] });
    this.activeStroke = undefined;
    this.activePointerId = undefined;
    this.emitChange();
  }

  onViewportWheel(event: WheelEvent): void {
    if (!this.imageLoaded) return;
    const viewport = this.surfaceRef?.nativeElement;
    if (!viewport) return;
    const rect = viewport.getBoundingClientRect();
    const factor = Math.exp(-event.deltaY * 0.002);
    this.zoomImageViewAt(
      this.imageViewScale * factor,
      event.clientX - rect.left,
      event.clientY - rect.top,
    );
    event.preventDefault();
  }

  resetImageView(): void {
    this.layoutImageInViewport();
  }

  rotateImageClockwise(): void {
    if (!this.imageLoaded) return;
    this.recordHistory();
    const nextRotation = ((this.imageRotation + 90) % 360) as ImageRotation;
    if (nextRotation === 0) delete this.document.rotation;
    else this.document.rotation = nextRotation;
    this.layoutImageInViewport();
    this.changeDetector.detectChanges();
    requestAnimationFrame(() => this.resizeCanvas());
    this.renderAndEmit();
  }

  appendMeasurementKey(key: string): void {
    if (key === '-' && this.measurementInput.includes('-')) return;
    if (key === '.' && this.measurementInput.includes('.')) return;
    if (key === '-' && this.measurementInput.length) return;
    this.measurementInput += key;
  }

  deleteMeasurementKey(): void { this.measurementInput = this.measurementInput.slice(0, -1); }

  confirmMeasurement(): void {
    const value = Number(this.measurementInput);
    const point = this.pendingMeasurementPoint;
    if (!point || !Number.isFinite(value) || !this.measurementInput.trim()) return;
    this.recordHistory();
    const annotation: MeasurementAnnotation = {
      id: this.createId(),
      type: 'measurement',
      measurementType: getMeasurementUnitDefinition(this.selectedMeasurementUnit).category,
      value,
      unit: this.selectedMeasurementUnit,
      color: this.selectedColor,
      point,
      labelPosition: getAutomaticMarkerLabelPosition(
        point,
        this.document.baseImage.width,
        this.document.baseImage.height,
        this.getOccupiedLabelPositions(),
      ),
      orientation: this.orientationForCurrentView(),
    };
    this.document.annotations.push(annotation);
    this.cancelMeasurement();
    this.renderAndEmit();
  }

  cancelMeasurement(): void {
    this.pendingMeasurementPoint = undefined;
    this.measurementInput = '';
  }

  private startBlurMove(event: PointerEvent, point: AnnotationPoint): boolean {
    if (event.pointerType === 'mouse' && event.button !== 0) return false;
    const blur = [...this.document.annotations].reverse().find(annotation => (
      annotation.type === 'blur' && this.isBlurHit(annotation, point)
    ));
    if (!blur || blur.type !== 'blur') return false;
    this.selectedAnnotationId = blur.id;
    this.activeBlurMove = {
      pointerId: event.pointerId,
      annotationId: blur.id,
      offsetX: point.x - blur.x,
      offsetY: point.y - blur.y,
      startX: point.x,
      startY: point.y,
      moved: false,
    };
    this.canvasRef?.nativeElement.setPointerCapture(event.pointerId);
    this.render();
    event.preventDefault();
    return true;
  }

  private moveBlur(event: PointerEvent): void {
    const drag = this.activeBlurMove;
    const point = this.toImagePoint(event);
    if (!drag || !point) return;
    if (!drag.moved && Math.hypot(point.x - drag.startX, point.y - drag.startY) < 1) return;
    if (!drag.moved) {
      this.recordHistory();
      drag.moved = true;
    }
    const blur = this.document.annotations.find(annotation => annotation.id === drag.annotationId);
    if (!blur || blur.type !== 'blur') return;
    const position = clampBlurPosition({
      x: point.x - drag.offsetX,
      y: point.y - drag.offsetY,
    }, blur, this.document.baseImage);
    blur.x = position.x;
    blur.y = position.y;
    this.render();
    event.preventDefault();
  }

  private endBlurMove(event: PointerEvent): void {
    const drag = this.activeBlurMove;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const canvas = this.canvasRef?.nativeElement;
    if (canvas?.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
    this.activeBlurMove = undefined;
    if (drag.moved) this.emitChange();
    event.preventDefault();
  }

  private isBlurHit(blur: BlurAnnotation, point: AnnotationPoint): boolean {
    return point.x >= blur.x
      && point.x <= blur.x + blur.width
      && point.y >= blur.y
      && point.y <= blur.y + blur.height;
  }

  private startTextMarkerPointDrag(event: PointerEvent, point: AnnotationPoint): boolean {
    if (event.pointerType === 'mouse' && event.button !== 0) return false;
    const tolerance = this.screenPixelsToImage(18);
    const marker = [...this.document.annotations].reverse().find(annotation => (
      annotation.type === 'marker'
      && annotation.markerSet === 'text'
      && !!annotation.point
      && this.distance(annotation.point, point) <= tolerance
    ));
    if (!marker || marker.type !== 'marker') return false;
    this.selectedAnnotationId = marker.id;
    this.activeTextMarkerPointDrag = {
      pointerId: event.pointerId,
      annotationId: marker.id,
      startX: point.x,
      startY: point.y,
      moved: false,
    };
    this.canvasRef?.nativeElement.setPointerCapture(event.pointerId);
    this.render();
    event.preventDefault();
    return true;
  }

  private moveTextMarkerPointDrag(event: PointerEvent): void {
    const drag = this.activeTextMarkerPointDrag;
    const point = this.toImagePoint(event);
    if (!drag || !point) return;
    if (!drag.moved && Math.hypot(point.x - drag.startX, point.y - drag.startY) < 1) return;
    if (!drag.moved) {
      this.recordHistory();
      drag.moved = true;
    }
    const marker = this.document.annotations.find(annotation => annotation.id === drag.annotationId);
    if (!marker || marker.type !== 'marker' || marker.markerSet !== 'text') return;
    marker.point = {
      x: Math.max(0, Math.min(this.document.baseImage.width, point.x)),
      y: Math.max(0, Math.min(this.document.baseImage.height, point.y)),
    };
    this.render();
    event.preventDefault();
  }

  private endTextMarkerPointDrag(event: PointerEvent): void {
    const drag = this.activeTextMarkerPointDrag;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const canvas = this.canvasRef?.nativeElement;
    if (canvas?.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
    this.activeTextMarkerPointDrag = undefined;
    if (drag.moved) this.emitChange();
    event.preventDefault();
  }

  private startLabelDrag(event: PointerEvent, point: AnnotationPoint): boolean {
    if (event.pointerType === 'mouse' && event.button !== 0) return false;
    const annotation = [...this.document.annotations].reverse().find(item => this.isLabelHit(item, point));
    if (!annotation || (annotation.type !== 'marker' && annotation.type !== 'measurement')) return false;
    const labelPosition = annotation.type === 'marker'
      ? normalizeMarkerAnnotation(annotation, this.document.baseImage.width, this.document.baseImage.height).labelPosition
      : annotation.labelPosition;
    this.selectedAnnotationId = annotation.id;
    this.activeLabelDrag = {
      pointerId: event.pointerId,
      annotationId: annotation.id,
      offsetX: point.x - labelPosition.x,
      offsetY: point.y - labelPosition.y,
      startX: point.x,
      startY: point.y,
      moved: false,
    };
    this.canvasRef?.nativeElement.setPointerCapture(event.pointerId);
    this.render();
    event.preventDefault();
    return true;
  }

  private moveLabelDrag(event: PointerEvent): void {
    const drag = this.activeLabelDrag;
    const point = this.toImagePoint(event);
    if (!drag || !point) return;
    if (!drag.moved && Math.hypot(point.x - drag.startX, point.y - drag.startY) < 1) return;
    if (!drag.moved) {
      this.recordHistory();
      drag.moved = true;
    }
    const annotation = this.document.annotations.find(item => item.id === drag.annotationId);
    if (!annotation || (annotation.type !== 'marker' && annotation.type !== 'measurement')) return;
    annotation.labelPosition = this.clampLabelPosition(
      annotation,
      point.x - drag.offsetX,
      point.y - drag.offsetY,
    );
    this.render();
    event.preventDefault();
  }

  private endLabelDrag(event: PointerEvent): void {
    const drag = this.activeLabelDrag;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const canvas = this.canvasRef?.nativeElement;
    if (canvas?.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
    this.activeLabelDrag = undefined;
    if (drag.moved) this.emitChange();
    event.preventDefault();
  }

  private isLabelHit(annotation: ImageAnnotation, point: AnnotationPoint): boolean {
    const shortSide = Math.min(this.document.baseImage.width, this.document.baseImage.height);
    const size = Math.max(32, shortSide * 0.035);
    const tolerance = this.screenPixelsToImage(10);
    if (annotation.type === 'marker') {
      const marker = normalizeMarkerAnnotation(
        annotation,
        this.document.baseImage.width,
        this.document.baseImage.height,
      );
      if (marker.markerSet === 'text') {
        const bounds = this.getTextMarkerLabelBounds(marker);
        return this.pointInsideOrientedBounds(
          point,
          bounds,
          marker.labelPosition,
          marker.orientation ?? 0,
          tolerance,
        );
      }
      const markerSize = Math.max(38, shortSide * 0.042);
      return this.distance(marker.labelPosition, point) <= markerSize * 0.75 + tolerance;
    }
    if (annotation.type !== 'measurement') return false;
    const text = `${annotation.value}${annotation.unit}`;
    const width = Math.max(size * 1.5, text.length * size * 0.68);
    return this.pointInsideOrientedBounds(point, {
      x: annotation.labelPosition.x,
      y: annotation.labelPosition.y - size * 1.3,
      width,
      height: size * 1.3,
    }, annotation.labelPosition, annotation.orientation ?? 0, tolerance);
  }

  private clampLabelPosition(
    annotation: MarkerAnnotation | LegacyMarkerAnnotation | MeasurementAnnotation,
    x: number,
    y: number,
  ): AnnotationPoint {
    const { width, height } = this.document.baseImage;
    const shortSide = Math.min(width, height);
    if (annotation.type === 'marker') {
      const marker = normalizeMarkerAnnotation(annotation, width, height);
      if (marker.markerSet === 'text') {
        const bounds = this.getTextMarkerLabelBounds(marker);
        return {
          x: Math.max(0, Math.min(width - bounds.width, x)),
          y: Math.max(bounds.height, Math.min(height, y)),
        };
      }
      const size = Math.max(38, shortSide * 0.042);
      const margin = size * 0.78;
      return {
        x: Math.max(margin, Math.min(width - margin, x)),
        y: Math.max(margin, Math.min(height - margin, y)),
      };
    }
    const size = Math.max(32, shortSide * 0.035);
    const text = `${annotation.value}${annotation.unit}`;
    const textWidth = Math.max(size * 1.5, text.length * size * 0.68);
    return {
      x: Math.max(0, Math.min(width - textWidth, x)),
      y: Math.max(size * 1.3, Math.min(height, y)),
    };
  }

  private getOccupiedLabelPositions(): AnnotationPoint[] {
    return this.document.annotations.flatMap(annotation => {
      if (annotation.type === 'measurement') return [{ ...annotation.labelPosition }];
      if (annotation.type === 'marker') {
        return [{ ...normalizeMarkerAnnotation(
          annotation,
          this.document.baseImage.width,
          this.document.baseImage.height,
        ).labelPosition }];
      }
      return [];
    });
  }

  private getTextMarkerLabelBounds(marker: MarkerAnnotation): {
    x: number;
    y: number;
    width: number;
    height: number;
    size: number;
  } {
    const size = Math.max(32, Math.min(this.document.baseImage.width, this.document.baseImage.height) * 0.035);
    const horizontalPadding = size * 0.48;
    const width = Math.max(size * 2, this.measureTextMarkerWidth(marker.text, size) + horizontalPadding * 2);
    const height = size * 1.5;
    return {
      x: marker.labelPosition.x,
      y: marker.labelPosition.y - height,
      width,
      height,
      size,
    };
  }

  private measureTextMarkerWidth(text: string, size: number): number {
    if (!this.textMeasureContext && typeof document !== 'undefined') {
      this.textMeasureContext = document.createElement('canvas').getContext('2d') ?? undefined;
    }
    if (!this.textMeasureContext) return Array.from(text).length * size;
    this.textMeasureContext.font = `700 ${size}px system-ui, sans-serif`;
    return this.textMeasureContext.measureText(text).width;
  }

  onPaletteDragStart(event: PointerEvent): void {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const palette = this.toolPaletteRef?.nativeElement;
    if (!palette) return;
    const rect = palette.getBoundingClientRect();
    this.paletteDrag = {
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };
    this.paletteDragging = true;
    this.activePopover = undefined;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    event.preventDefault();
    event.stopPropagation();
  }

  onPaletteDragMove(event: PointerEvent): void {
    const drag = this.paletteDrag;
    const surface = this.surfaceRef?.nativeElement;
    const palette = this.toolPaletteRef?.nativeElement;
    if (!drag || drag.pointerId !== event.pointerId || !surface || !palette) return;
    const surfaceRect = surface.getBoundingClientRect();
    this.palettePosition = clampFloatingPalettePosition({
      x: event.clientX - surfaceRect.left - drag.offsetX,
      y: event.clientY - surfaceRect.top - drag.offsetY,
    }, {
      width: surface.clientWidth,
      height: surface.clientHeight,
    }, {
      width: palette.offsetWidth,
      height: palette.offsetHeight,
    });
    if (this.pendingMeasurementPoint) this.scheduleFloatingPanelPosition();
    event.preventDefault();
    event.stopPropagation();
  }

  onPaletteDragEnd(event: PointerEvent): void {
    if (!this.paletteDrag || this.paletteDrag.pointerId !== event.pointerId) return;
    const handle = event.currentTarget as HTMLElement;
    if (handle.hasPointerCapture(event.pointerId)) handle.releasePointerCapture(event.pointerId);
    this.paletteDrag = undefined;
    this.paletteDragging = false;
    event.preventDefault();
    event.stopPropagation();
  }

  @HostListener('window:resize')
  onViewportResize(): void {
    requestAnimationFrame(() => {
      this.clampPaletteToSurface();
      this.scheduleFloatingPanelPosition();
    });
  }

  private handleVisualViewportResize(): void {
    const viewportHeight = window.visualViewport?.height;
    const baseline = this.textMarkerViewportBaseline;
    const previousHeight = this.textMarkerLastViewportHeight;
    if (
      viewportHeight === undefined
      || baseline === undefined
      || !this.pendingTextMarker
      || this.cancelTextMarkerRequested
    ) return;
    this.textMarkerLastViewportHeight = viewportHeight;
    if (baseline - viewportHeight > 80) {
      this.textMarkerKeyboardWasVisible = true;
      return;
    }
    const keyboardWasClosed = this.textMarkerKeyboardWasVisible
      && (viewportHeight >= baseline - 60 || viewportHeight - (previousHeight ?? viewportHeight) > 80);
    if (!keyboardWasClosed || !this.normalizedTextMarkerInput) return;
    this.textMarkerKeyboardWasVisible = false;
    // iOSのキーボード収納ボタンはinputをblurしないため、Viewportの復元を保存契機にする。
    queueMicrotask(() => this.confirmTextMarker());
  }

  private resetTextMarkerKeyboardTracking(): void {
    this.textMarkerViewportBaseline = undefined;
    this.textMarkerLastViewportHeight = undefined;
    this.textMarkerKeyboardWasVisible = false;
  }

  private async loadHtmlImage(src: string): Promise<HTMLImageElement> {
    const image = new Image();
    if (/^https?:\/\//i.test(src) && !src.startsWith(location.origin)) image.crossOrigin = 'anonymous';
    image.decoding = 'async';
    const loaded = new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('Image loading failed.'));
    });
    image.src = src;
    await loaded;
    if (typeof image.decode === 'function') await image.decode().catch(() => undefined);
    return image;
  }

  private resizeCanvas(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) return;
    const widthCss = canvas.clientWidth;
    const heightCss = canvas.clientHeight;
    if (!widthCss || !heightCss) return;
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const width = Math.max(1, Math.round(widthCss * ratio));
    const height = Math.max(1, Math.round(heightCss * ratio));
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
    this.render();
  }

  private get rotatedDisplayWidth(): number {
    return this.imageRotation === 90 || this.imageRotation === 270
      ? this.imageDisplayHeight
      : this.imageDisplayWidth;
  }

  private get rotatedDisplayHeight(): number {
    return this.imageRotation === 90 || this.imageRotation === 270
      ? this.imageDisplayWidth
      : this.imageDisplayHeight;
  }

  private handleSurfaceResize(resetPalette = false): void {
    this.layoutImageInViewport();
    this.changeDetector.detectChanges();
    requestAnimationFrame(() => {
      this.resizeCanvas();
      this.clampPaletteToSurface(resetPalette);
      this.scheduleFloatingPanelPosition();
    });
  }

  private layoutImageInViewport(): void {
    const viewport = this.surfaceRef?.nativeElement;
    const { width, height } = this.document.baseImage;
    if (!viewport || !width || !height || !viewport.clientWidth || !viewport.clientHeight) return;
    const rotatedSize = getRotatedImageSize({ width, height }, this.imageRotation);
    const fitScale = Math.min(
      viewport.clientWidth / rotatedSize.width,
      viewport.clientHeight / rotatedSize.height,
    );
    this.imageDisplayWidth = Math.max(1, width * fitScale);
    this.imageDisplayHeight = Math.max(1, height * fitScale);
    this.imageViewScale = 1;
    this.imageViewX = (viewport.clientWidth - this.rotatedDisplayWidth) / 2;
    this.imageViewY = (viewport.clientHeight - this.rotatedDisplayHeight) / 2;
    this.imageViewPointers.clear();
    this.imageViewGesture = undefined;
    this.changeDetector.markForCheck();
  }

  private startImageViewGesture(event: PointerEvent): void {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.imageViewPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    this.canvasRef?.nativeElement.setPointerCapture(event.pointerId);
    this.restartImageViewGesture();
    event.preventDefault();
  }

  private moveImageViewGesture(event: PointerEvent): void {
    if (!this.imageViewPointers.has(event.pointerId)) return;
    this.imageViewPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = [...this.imageViewPointers.values()];
    const gesture = this.imageViewGesture;
    const viewport = this.surfaceRef?.nativeElement;
    if (!gesture || !viewport || !points.length) return;
    const center = this.getPointerCenter(points, viewport.getBoundingClientRect());
    if (points.length === 1 || gesture.distance === undefined) {
      this.imageViewX += center.x - gesture.centerX;
      this.imageViewY += center.y - gesture.centerY;
      gesture.centerX = center.x;
      gesture.centerY = center.y;
      this.clampImageView();
    } else {
      const distance = this.getPointerDistance(points);
      const nextScale = gesture.scale * distance / Math.max(1, gesture.distance);
      this.zoomImageViewAt(nextScale, center.x, center.y, gesture.anchorX, gesture.anchorY);
    }
    event.preventDefault();
  }

  private endImageViewGesture(event: PointerEvent): void {
    const canvas = this.canvasRef?.nativeElement;
    if (canvas?.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
    this.imageViewPointers.delete(event.pointerId);
    this.restartImageViewGesture();
    event.preventDefault();
  }

  private restartImageViewGesture(): void {
    const viewport = this.surfaceRef?.nativeElement;
    const points = [...this.imageViewPointers.values()];
    if (!viewport || !points.length) {
      this.imageViewGesture = undefined;
      return;
    }
    const center = this.getPointerCenter(points, viewport.getBoundingClientRect());
    this.imageViewGesture = {
      centerX: center.x,
      centerY: center.y,
      distance: points.length > 1 ? this.getPointerDistance(points) : undefined,
      scale: this.imageViewScale,
      anchorX: (center.x - this.imageViewX) / this.imageViewScale,
      anchorY: (center.y - this.imageViewY) / this.imageViewScale,
    };
  }

  private zoomImageViewAt(scale: number, centerX: number, centerY: number, anchorX?: number, anchorY?: number): void {
    const nextScale = Math.max(1, Math.min(8, scale));
    const imageX = anchorX ?? (centerX - this.imageViewX) / this.imageViewScale;
    const imageY = anchorY ?? (centerY - this.imageViewY) / this.imageViewScale;
    this.imageViewScale = nextScale;
    this.imageViewX = centerX - imageX * nextScale;
    this.imageViewY = centerY - imageY * nextScale;
    this.clampImageView();
  }

  private clampImageView(): void {
    const viewport = this.surfaceRef?.nativeElement;
    if (!viewport) return;
    const position = clampImageViewportPosition(
      { x: this.imageViewX, y: this.imageViewY },
      { width: viewport.clientWidth, height: viewport.clientHeight },
      { width: this.rotatedDisplayWidth, height: this.rotatedDisplayHeight },
      this.imageViewScale,
    );
    this.imageViewX = position.x;
    this.imageViewY = position.y;
    this.changeDetector.markForCheck();
  }

  private getPointerCenter(points: readonly { x: number; y: number }[], viewport: DOMRect): { x: number; y: number } {
    const total = points.reduce((sum, point) => ({ x: sum.x + point.x, y: sum.y + point.y }), { x: 0, y: 0 });
    return { x: total.x / points.length - viewport.left, y: total.y / points.length - viewport.top };
  }

  private getPointerDistance(points: readonly { x: number; y: number }[]): number {
    return points.length > 1 ? Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y) : 0;
  }

  private clampPaletteToSurface(reset = false): void {
    const surface = this.surfaceRef?.nativeElement;
    const palette = this.toolPaletteRef?.nativeElement;
    if (!surface || !palette || !surface.clientWidth || !surface.clientHeight) return;
    const containerSize = { width: surface.clientWidth, height: surface.clientHeight };
    const paletteSize = { width: palette.offsetWidth, height: palette.offsetHeight };
    this.palettePosition = reset || !this.palettePosition
      ? getInitialFloatingPalettePosition(containerSize, paletteSize)
      : clampFloatingPalettePosition(this.palettePosition, containerSize, paletteSize);
    this.changeDetector.markForCheck();
  }

  private scheduleFloatingPanelPosition(): void {
    if (this.floatingPanelFrame !== undefined) cancelAnimationFrame(this.floatingPanelFrame);
    this.floatingPanelFrame = requestAnimationFrame(() => {
      this.floatingPanelFrame = undefined;
      this.positionFloatingPanel();
    });
  }

  private positionFloatingPanel(): void {
    const viewport = this.surfaceRef?.nativeElement;
    const palette = this.toolPaletteRef?.nativeElement;
    const panel = this.floatingPanelRef?.nativeElement;
    if (!viewport || !panel) return;

    if (this.activePopover === 'text-marker') {
      // 画像のzoom/panや下部キーボードに影響されず、入力欄を常に編集面上部へ表示する。
      this.floatingPanelPosition = {
        x: Math.max(8, (viewport.clientWidth - panel.offsetWidth) / 2),
        y: 12,
      };
      this.changeDetector.markForCheck();
      return;
    }
    if (!palette) return;

    const gap = 8;
    const viewportRect = viewport.getBoundingClientRect();
    const paletteRect = palette.getBoundingClientRect();
    this.floatingPanelPosition = getAdjacentFloatingPanelPosition(
      { width: viewport.clientWidth, height: viewport.clientHeight },
      {
        x: paletteRect.left - viewportRect.left,
        y: paletteRect.top - viewportRect.top,
        width: paletteRect.width,
        height: paletteRect.height,
      },
      { width: panel.offsetWidth, height: panel.offsetHeight },
      gap,
    );
    this.changeDetector.markForCheck();
  }

  private render(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, canvas.width, canvas.height);
    if (!this.imageLoaded) return;
    context.save();
    context.scale(canvas.width / this.document.baseImage.width, canvas.height / this.document.baseImage.height);
    this.drawAnnotations(context, this.document.annotations);
    if (this.pendingTextMarker) {
      const previewText = this.normalizedTextMarkerInput || 'テキスト';
      this.drawTextMarker(context, {
        id: 'pending-text-marker',
        type: 'marker',
        markerSet: 'text',
        value: previewText,
        text: previewText,
        color: this.selectedColor,
        point: this.pendingTextMarker.point,
        labelPosition: this.pendingTextMarker.labelPosition,
        orientation: this.pendingTextMarker.orientation,
      });
    }
    if (this.pendingMeasurementPoint) this.drawPendingPoint(context, this.pendingMeasurementPoint);
    context.restore();
  }

  private get normalizedTextMarkerInput(): string {
    return this.textMarkerInput.replace(/\s+/g, ' ').trim();
  }

  private drawAnnotations(context: CanvasRenderingContext2D, annotations: readonly ImageAnnotation[]): void {
    annotations.forEach(annotation => {
      if (annotation.type === 'blur') this.drawBlur(context, annotation);
    });
    annotations.forEach(annotation => {
      if (annotation.type === 'blur') return;
      if (annotation.type === 'stroke') this.drawStroke(context, annotation);
      else if (annotation.type === 'measurement') this.drawMeasurement(context, annotation);
      else if (annotation.type === 'marker') this.drawMarker(context, annotation);
      else this.drawLabel(context, annotation.text, annotation.point, annotation.color);
    });
  }

  private drawBlur(context: CanvasRenderingContext2D, blur: BlurAnnotation): void {
    if (!this.imageElement || blur.width <= 0 || blur.height <= 0) return;
    // Safari/PWAではCanvasRenderingContext2D.filterが反映されない端末がある。
    // 選択範囲を十分に縮小してから補間拡大し、filter非依存で判別不能にする。
    const sampleStep = Math.max(8, blur.radius * 0.5);
    const sampleWidth = Math.max(1, Math.ceil(blur.width / sampleStep));
    const sampleHeight = Math.max(1, Math.ceil(blur.height / sampleStep));
    const scratch = this.blurScratchCanvas ??= document.createElement('canvas');
    scratch.width = sampleWidth;
    scratch.height = sampleHeight;
    const scratchContext = scratch.getContext('2d');
    if (!scratchContext) return;
    scratchContext.clearRect(0, 0, sampleWidth, sampleHeight);
    scratchContext.imageSmoothingEnabled = true;
    scratchContext.imageSmoothingQuality = 'high';
    scratchContext.drawImage(
      this.imageElement,
      blur.x,
      blur.y,
      blur.width,
      blur.height,
      0,
      0,
      sampleWidth,
      sampleHeight,
    );
    context.save();
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(scratch, 0, 0, sampleWidth, sampleHeight, blur.x, blur.y, blur.width, blur.height);
    context.restore();
    if (blur.id === this.activeBlur?.id || blur.id === this.selectedAnnotationId) {
      context.save();
      context.strokeStyle = '#ffffff';
      context.lineWidth = Math.max(4, Math.min(this.document.baseImage.width, this.document.baseImage.height) * 0.003);
      context.setLineDash([12, 10]);
      context.strokeRect(blur.x, blur.y, blur.width, blur.height);
      context.restore();
    }
  }

  private drawStroke(context: CanvasRenderingContext2D, stroke: StrokeAnnotation): void {
    if (!stroke.points.length) return;
    context.save();
    context.strokeStyle = stroke.color;
    context.fillStyle = stroke.color;
    context.globalAlpha = stroke.opacity;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.lineWidth = stroke.width;
    const mode = stroke.mode ?? 'freehand';
    const hasLength = stroke.points.some(point => (
      point.x !== stroke.points[0].x || point.y !== stroke.points[0].y
    ));
    if (!hasLength) {
      context.beginPath();
      context.arc(stroke.points[0].x, stroke.points[0].y, stroke.width / 2, 0, Math.PI * 2);
      context.fill();
      context.restore();
      return;
    }
    const start = stroke.points[0];
    const end = stroke.points[stroke.points.length - 1];
    context.beginPath();
    if (mode === 'ellipse') {
      context.ellipse(
        (start.x + end.x) / 2,
        (start.y + end.y) / 2,
        Math.abs(end.x - start.x) / 2,
        Math.abs(end.y - start.y) / 2,
        0,
        0,
        Math.PI * 2,
      );
    } else if (mode === 'rectangle') {
      context.rect(start.x, start.y, end.x - start.x, end.y - start.y);
    } else if (mode === 'line' || mode === 'highlighter') {
      context.moveTo(start.x, start.y);
      context.lineTo(end.x, end.y);
    } else {
      context.moveTo(start.x, start.y);
      for (let index = 1; index < stroke.points.length; index += 1) {
        const point = stroke.points[index];
        context.lineTo(point.x, point.y);
      }
    }
    context.stroke();
    context.restore();
  }

  private drawMeasurement(context: CanvasRenderingContext2D, item: MeasurementAnnotation): void {
    const unit = item.unit;
    const text = `${item.value}${unit}`;
    const size = Math.max(32, Math.min(this.document.baseImage.width, this.document.baseImage.height) * 0.035);
    const lineWidth = Math.max(5, size * 0.12);
    const labelWidth = Math.max(size * 1.5, text.length * size * 0.68);
    const labelBounds = {
      x: item.labelPosition.x,
      y: item.labelPosition.y - size * 1.2,
      width: labelWidth,
      height: size * 1.45,
    };
    const orientation = item.orientation ?? 0;
    const connectionPoint = this.getOrientedLabelConnectionPoint(
      item.point,
      labelBounds,
      item.labelPosition,
      orientation,
    );
    context.save();
    context.strokeStyle = item.color;
    context.fillStyle = item.color;
    context.lineWidth = lineWidth;
    context.lineCap = 'round';
    context.beginPath();
    context.moveTo(item.point.x, item.point.y);
    context.lineTo(connectionPoint.x, connectionPoint.y);
    context.stroke();
    context.beginPath();
    context.arc(item.point.x, item.point.y, lineWidth * 1.4, 0, Math.PI * 2);
    context.fill();
    this.drawLabel(context, text, item.labelPosition, item.color, orientation);
    if (item.id === this.selectedAnnotationId) {
      this.withOrientation(context, item.labelPosition, orientation, () => {
        this.drawSelectionBox(context, labelBounds.x, labelBounds.y, labelBounds.width, labelBounds.height, size);
      });
    }
    context.restore();
  }

  private drawMarker(context: CanvasRenderingContext2D, source: MarkerAnnotation | LegacyMarkerAnnotation): void {
    const marker = normalizeMarkerAnnotation(
      source,
      this.document.baseImage.width,
      this.document.baseImage.height,
    );
    if (marker.markerSet === 'text') {
      this.drawTextMarker(context, marker);
      return;
    }
    const size = Math.max(38, Math.min(this.document.baseImage.width, this.document.baseImage.height) * 0.042);
    const radius = size * 0.62;
    const pointRadius = Math.max(5, size * 0.13);
    const outline = marker.color.toLowerCase() === '#ffffff' ? '#333333' : '#ffffff';
    const connectionPoint = getLabelConnectionPoint(marker.point, {
      x: marker.labelPosition.x - radius,
      y: marker.labelPosition.y - radius,
      width: radius * 2,
      height: radius * 2,
    }, 'ellipse');
    context.save();
    context.strokeStyle = marker.color;
    context.lineWidth = Math.max(4, size * 0.09);
    context.lineCap = 'round';
    context.beginPath();
    context.moveTo(marker.point.x, marker.point.y);
    context.lineTo(connectionPoint.x, connectionPoint.y);
    context.stroke();
    context.fillStyle = marker.color;
    context.beginPath();
    context.arc(marker.point.x, marker.point.y, pointRadius, 0, Math.PI * 2);
    context.fill();
    context.strokeStyle = outline;
    context.lineWidth = Math.max(2, size * 0.05);
    context.stroke();
    context.fillStyle = 'rgba(0, 0, 0, 0.48)';
    context.strokeStyle = outline;
    context.lineWidth = Math.max(4, size * 0.09);
    context.beginPath();
    context.arc(marker.labelPosition.x, marker.labelPosition.y, radius, 0, Math.PI * 2);
    context.fill();
    context.stroke();
    this.withOrientation(context, marker.labelPosition, marker.orientation ?? 0, () => {
      context.fillStyle = marker.color;
      context.font = `700 ${size}px system-ui, sans-serif`;
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(marker.text, marker.labelPosition.x, marker.labelPosition.y + size * 0.03);
    });
    if (marker.id === this.selectedAnnotationId) {
      context.strokeStyle = '#ffffff';
      context.lineWidth = Math.max(4, size * 0.08);
      context.setLineDash([size * 0.2, size * 0.14]);
      context.beginPath();
      context.arc(marker.labelPosition.x, marker.labelPosition.y, radius * 1.22, 0, Math.PI * 2);
      context.stroke();
    }
    context.restore();
  }

  private drawTextMarker(context: CanvasRenderingContext2D, marker: MarkerAnnotation): void {
    const bounds = this.getTextMarkerLabelBounds(marker);
    const orientation = marker.orientation ?? 0;
    const connectionPoint = this.getOrientedLabelConnectionPoint(
      marker.point,
      bounds,
      marker.labelPosition,
      orientation,
    );
    context.save();
    context.strokeStyle = marker.color;
    context.fillStyle = marker.color;
    context.lineWidth = Math.max(4, bounds.size * 0.12);
    context.lineCap = 'round';
    context.beginPath();
    context.moveTo(connectionPoint.x, connectionPoint.y);
    context.lineTo(marker.point.x, marker.point.y);
    context.stroke();
    this.drawArrowHead(context, connectionPoint, marker.point, bounds.size);

    this.withOrientation(context, marker.labelPosition, orientation, () => {
      context.fillStyle = 'rgba(0, 0, 0, 0.58)';
      context.strokeStyle = marker.color;
      context.lineWidth = Math.max(3, bounds.size * 0.07);
      this.traceRoundedRectangle(
        context,
        bounds.x,
        bounds.y,
        bounds.width,
        bounds.height,
        Math.min(bounds.size * 0.22, bounds.height / 2),
      );
      context.fill();
      context.stroke();
      context.fillStyle = '#ffffff';
      context.font = `700 ${bounds.size}px system-ui, sans-serif`;
      context.textAlign = 'left';
      context.textBaseline = 'middle';
      context.fillText(marker.text, bounds.x + bounds.size * 0.48, bounds.y + bounds.height / 2 + bounds.size * 0.03);
      if (marker.id === this.selectedAnnotationId) {
        this.drawSelectionBox(context, bounds.x, bounds.y, bounds.width, bounds.height, bounds.size);
      }
    });
    context.restore();
  }

  private traceRoundedRectangle(
    context: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
  ): void {
    const safeRadius = Math.max(0, Math.min(radius, width / 2, height / 2));
    context.beginPath();
    context.moveTo(x + safeRadius, y);
    context.lineTo(x + width - safeRadius, y);
    context.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
    context.lineTo(x + width, y + height - safeRadius);
    context.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
    context.lineTo(x + safeRadius, y + height);
    context.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
    context.lineTo(x, y + safeRadius);
    context.quadraticCurveTo(x, y, x + safeRadius, y);
    context.closePath();
  }

  private drawArrowHead(
    context: CanvasRenderingContext2D,
    from: AnnotationPoint,
    tip: AnnotationPoint,
    labelSize: number,
  ): void {
    const angle = Math.atan2(tip.y - from.y, tip.x - from.x);
    const length = Math.max(18, labelSize * 0.54);
    const spread = Math.PI / 6;
    context.beginPath();
    context.moveTo(tip.x, tip.y);
    context.lineTo(
      tip.x - length * Math.cos(angle - spread),
      tip.y - length * Math.sin(angle - spread),
    );
    context.lineTo(
      tip.x - length * Math.cos(angle + spread),
      tip.y - length * Math.sin(angle + spread),
    );
    context.closePath();
    context.fill();
  }

  private orientationForCurrentView(): ImageRotation {
    return ((360 - this.imageRotation) % 360) as ImageRotation;
  }

  private rotatePointAround(
    point: AnnotationPoint,
    anchor: AnnotationPoint,
    rotation: ImageRotation,
  ): AnnotationPoint {
    const x = point.x - anchor.x;
    const y = point.y - anchor.y;
    if (rotation === 90) return { x: anchor.x - y, y: anchor.y + x };
    if (rotation === 180) return { x: anchor.x - x, y: anchor.y - y };
    if (rotation === 270) return { x: anchor.x + y, y: anchor.y - x };
    return { x: point.x, y: point.y };
  }

  private getOrientedLabelConnectionPoint(
    point: AnnotationPoint,
    bounds: { x: number; y: number; width: number; height: number },
    anchor: AnnotationPoint,
    orientation: ImageRotation,
  ): AnnotationPoint {
    const inverse = ((360 - orientation) % 360) as ImageRotation;
    const localPoint = this.rotatePointAround(point, anchor, inverse);
    const localConnection = getLabelConnectionPoint(localPoint, bounds);
    return this.rotatePointAround(localConnection, anchor, orientation);
  }

  private pointInsideOrientedBounds(
    point: AnnotationPoint,
    bounds: { x: number; y: number; width: number; height: number },
    anchor: AnnotationPoint,
    orientation: ImageRotation,
    tolerance = 0,
  ): boolean {
    const inverse = ((360 - orientation) % 360) as ImageRotation;
    const localPoint = this.rotatePointAround(point, anchor, inverse);
    return localPoint.x >= bounds.x - tolerance
      && localPoint.x <= bounds.x + bounds.width + tolerance
      && localPoint.y >= bounds.y - tolerance
      && localPoint.y <= bounds.y + bounds.height + tolerance;
  }

  private withOrientation(
    context: CanvasRenderingContext2D,
    anchor: AnnotationPoint,
    orientation: ImageRotation,
    draw: () => void,
  ): void {
    context.save();
    context.translate(anchor.x, anchor.y);
    context.rotate(orientation * Math.PI / 180);
    context.translate(-anchor.x, -anchor.y);
    draw();
    context.restore();
  }

  private drawSelectionBox(
    context: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    size: number,
  ): void {
    context.save();
    context.strokeStyle = '#ffffff';
    context.lineWidth = Math.max(4, size * 0.08);
    context.setLineDash([size * 0.2, size * 0.14]);
    context.strokeRect(x - size * 0.12, y - size * 0.12, width + size * 0.24, height + size * 0.24);
    context.restore();
  }

  private drawLabel(
    context: CanvasRenderingContext2D,
    text: string,
    point: AnnotationPoint,
    color: string,
    orientation: ImageRotation = 0,
  ): void {
    const size = Math.max(32, Math.min(this.document.baseImage.width, this.document.baseImage.height) * 0.035);
    this.withOrientation(context, point, orientation, () => {
      context.save();
      context.font = `700 ${size}px system-ui, sans-serif`;
      context.textBaseline = 'bottom';
      context.lineJoin = 'round';
      context.lineWidth = Math.max(4, size * 0.14);
      context.strokeStyle = color.toLowerCase() === '#ffffff' ? '#333333' : '#ffffff';
      context.strokeText(text, point.x, point.y);
      context.fillStyle = color;
      context.fillText(text, point.x, point.y);
      context.restore();
    });
  }

  private drawPendingPoint(context: CanvasRenderingContext2D, point: AnnotationPoint): void {
    const radius = Math.max(16, Math.min(this.document.baseImage.width, this.document.baseImage.height) * 0.012);
    context.save();
    context.fillStyle = this.selectedColor;
    context.strokeStyle = '#ffffff';
    context.lineWidth = Math.max(4, radius * 0.25);
    context.beginPath();
    context.arc(point.x, point.y, radius, 0, Math.PI * 2);
    context.fill();
    context.stroke();
    context.restore();
  }

  private eraseAt(point: AnnotationPoint): void {
    const tolerance = this.screenPixelsToImage(26);
    for (let index = this.document.annotations.length - 1; index >= 0; index -= 1) {
      if (!this.hitTest(this.document.annotations[index], point, tolerance)) continue;
      this.recordHistory();
      if (this.document.annotations[index].id === this.selectedAnnotationId) this.selectedAnnotationId = undefined;
      this.document.annotations.splice(index, 1);
      this.renderAndEmit();
      return;
    }
  }

  private hitTest(annotation: ImageAnnotation, point: AnnotationPoint, tolerance: number): boolean {
    if (annotation.type === 'blur') {
      return point.x >= annotation.x - tolerance
        && point.x <= annotation.x + annotation.width + tolerance
        && point.y >= annotation.y - tolerance
        && point.y <= annotation.y + annotation.height + tolerance;
    }
    if (annotation.type === 'stroke') {
      const radius = tolerance + annotation.width / 2;
      const mode = annotation.mode ?? 'freehand';
      if ((mode === 'ellipse' || mode === 'rectangle') && annotation.points.length > 1) {
        return this.hitTestStrokeShape(annotation, point, radius);
      }
      if ((mode === 'line' || mode === 'highlighter') && annotation.points.length > 1) {
        return this.distanceToSegment(
          point,
          annotation.points[0],
          annotation.points[annotation.points.length - 1],
        ) <= radius;
      }
      if (annotation.points.some(strokePoint => this.distance(strokePoint, point) <= radius)) return true;
      return annotation.points.slice(1).some((strokePoint, index) => (
        this.distanceToSegment(point, annotation.points[index], strokePoint) <= radius
      ));
    }
    if (annotation.type === 'measurement') {
      const size = Math.max(32, Math.min(this.document.baseImage.width, this.document.baseImage.height) * 0.035);
      const text = `${annotation.value}${annotation.unit}`;
      const bounds = {
        x: annotation.labelPosition.x,
        y: annotation.labelPosition.y - size * 1.2,
        width: Math.max(size * 1.5, text.length * size * 0.68),
        height: size * 1.45,
      };
      const connectionPoint = this.getOrientedLabelConnectionPoint(
        annotation.point,
        bounds,
        annotation.labelPosition,
        annotation.orientation ?? 0,
      );
      return this.distance(annotation.point, point) <= tolerance
        || this.pointInsideOrientedBounds(
          point,
          bounds,
          annotation.labelPosition,
          annotation.orientation ?? 0,
          tolerance,
        )
        || this.distanceToSegment(point, annotation.point, connectionPoint) <= tolerance;
    }
    if (annotation.type === 'marker') {
      const marker = normalizeMarkerAnnotation(
        annotation,
        this.document.baseImage.width,
        this.document.baseImage.height,
      );
      if (marker.markerSet === 'text') {
        const bounds = this.getTextMarkerLabelBounds(marker);
        const connectionPoint = this.getOrientedLabelConnectionPoint(
          marker.point,
          bounds,
          marker.labelPosition,
          marker.orientation ?? 0,
        );
        return this.pointInsideOrientedBounds(
          point,
          bounds,
          marker.labelPosition,
          marker.orientation ?? 0,
          tolerance,
        )
          || this.distance(marker.point, point) <= tolerance
          || this.distanceToSegment(point, marker.point, connectionPoint) <= tolerance;
      }
      return this.distance(marker.point, point) <= tolerance
        || this.distance(marker.labelPosition, point) <= tolerance * 1.8
        || this.distanceToSegment(point, marker.point, marker.labelPosition) <= tolerance;
    }
    return this.distance(annotation.point, point) <= tolerance * 2;
  }

  private hitTestStrokeShape(stroke: StrokeAnnotation, point: AnnotationPoint, tolerance: number): boolean {
    const start = stroke.points[0];
    const end = stroke.points[stroke.points.length - 1];
    const left = Math.min(start.x, end.x);
    const right = Math.max(start.x, end.x);
    const top = Math.min(start.y, end.y);
    const bottom = Math.max(start.y, end.y);
    if (stroke.mode === 'rectangle') {
      if (point.x < left - tolerance || point.x > right + tolerance
        || point.y < top - tolerance || point.y > bottom + tolerance) return false;
      return point.x <= left + tolerance || point.x >= right - tolerance
        || point.y <= top + tolerance || point.y >= bottom - tolerance;
    }
    const radiusX = (right - left) / 2;
    const radiusY = (bottom - top) / 2;
    if (radiusX <= tolerance || radiusY <= tolerance) {
      return this.distanceToSegment(point, start, end) <= tolerance;
    }
    const centerX = (left + right) / 2;
    const centerY = (top + bottom) / 2;
    const normalizedDistance = Math.hypot(
      (point.x - centerX) / radiusX,
      (point.y - centerY) / radiusY,
    );
    return Math.abs(normalizedDistance - 1) <= tolerance / Math.min(radiusX, radiusY);
  }

  private toImagePoint(event: PointerEvent): AnnotationPoint | undefined {
    const viewport = this.surfaceRef?.nativeElement;
    if (!viewport || !this.imageDisplayWidth || !this.imageDisplayHeight || !this.imageViewScale) return undefined;
    const viewportRect = viewport.getBoundingClientRect();
    const displayX = (event.clientX - viewportRect.left - this.imageViewX) / this.imageViewScale;
    const displayY = (event.clientY - viewportRect.top - this.imageViewY) / this.imageViewScale;
    const localPoint = mapRotatedPointToOriginal(
      { x: displayX, y: displayY },
      { width: this.imageDisplayWidth, height: this.imageDisplayHeight },
      this.imageRotation,
    );
    return {
      x: Math.max(0, Math.min(this.document.baseImage.width, localPoint.x * this.document.baseImage.width / this.imageDisplayWidth)),
      y: Math.max(0, Math.min(this.document.baseImage.height, localPoint.y * this.document.baseImage.height / this.imageDisplayHeight)),
      pressure: event.pressure > 0 ? event.pressure : undefined,
    };
  }

  private applyImageRotation(
    context: CanvasRenderingContext2D,
    rotation: ImageRotation,
    width: number,
    height: number,
  ): void {
    if (rotation === 90) {
      context.translate(height, 0);
      context.rotate(Math.PI / 2);
    } else if (rotation === 180) {
      context.translate(width, height);
      context.rotate(Math.PI);
    } else if (rotation === 270) {
      context.translate(0, width);
      context.rotate(-Math.PI / 2);
    }
  }

  private recordHistory(): void {
    this.undoStack.push(this.cloneDocument(this.document));
    if (this.undoStack.length > 50) this.undoStack.shift();
    this.redoStack = [];
  }

  private renderAndEmit(): void { this.render(); this.emitChange(); }
  private emitChange(): void { this.annotationDataChange.emit(this.getAnnotations()); }
  private distance(a: AnnotationPoint, b: AnnotationPoint): number { return Math.hypot(a.x - b.x, a.y - b.y); }
  private screenPixelsToImage(pixels: number): number {
    const displayScale = this.imageDisplayWidth * this.imageViewScale / Math.max(1, this.document.baseImage.width);
    return pixels / Math.max(0.0001, displayScale);
  }
  private distanceToSegment(point: AnnotationPoint, start: AnnotationPoint, end: AnnotationPoint): number {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    if (!dx && !dy) return this.distance(point, start);
    const projection = Math.max(0, Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy)));
    return Math.hypot(point.x - (start.x + projection * dx), point.y - (start.y + projection * dy));
  }
  private cloneDocument(data: ImageAnnotationDocument): ImageAnnotationDocument {
    return JSON.parse(JSON.stringify(data)) as ImageAnnotationDocument;
  }
  private emptyDocument(): ImageAnnotationDocument { return { version: 1, baseImage: { width: 0, height: 0 }, annotations: [] }; }
  private createId(): string { return globalThis.crypto?.randomUUID?.() ?? `annotation-${Date.now()}-${Math.random().toString(16).slice(2)}`; }
  private releaseObjectUrl(): void { if (this.objectUrl) URL.revokeObjectURL(this.objectUrl); this.objectUrl = undefined; }
}
